//@ts-nocheck
import { useState } from 'react';
import type { NextPage } from 'next';
import Link from 'next/link';
import Image from 'next/image';
import { BailetsBlock } from '../../ModalCopy.styles';
import { CloseVideoIcon, LogoMartins } from '@assets/svgs';
import { CWriting } from '@data/copywriting';

type DiorsauvageProps = {
  isClosed?: () => void;
  showImg?: boolean | string;
  showMedia?: boolean | string;
};

export const Diorsauvage = ({
  isClosed,
  showImg,
  showMedia
}: DiorsauvageProps) => {
  return (
    <>
      <BailetsBlock>
        <CloseVideoIcon
          animation="closeIcon"
          color="#000"
          isActive={isClosed}
        />
        <LogoMartins
          width={70}
          height={65}
          color="rgba(0,0,0,0.5)"
          animation="mansion"
        />

        <h3>DIOR</h3>
        <span style={{ marginBottom: '25px' }}>
          SAUVAGE - EAU DE PERFUM - POUR HOMME
        </span>
        {showImg && <Image src={showImg} width={1480} height={825} alt="" />}
        {showMedia && (
          <video
            controlsList="nodownload"
            controls={true}
            playsInline
            muted
            autoPlay
            loop
          >
            <source src={showMedia} type="video/mp4" />
          </video>
        )}
        <h3 style={{ marginTop: '25px' }}>{CWriting.copyWriting[1].title1}</h3>
        <h3>{CWriting.copyWriting[1].title2}</h3>
        <p>
          <b>
            Irresistible for confident men. Only open-minders know the good
            taste of being lush. RE-DESIGN YOURSELF TO BE FREE.
          </b>
          <br />
          <br /> Embrace the wild sophistication of Dior Sauvage, where the
          lively brightness of Calabria bergamot intertwines with the
          captivating depth of ambroxan. Meticulously crafted with the artistry
          synonymous with Dior, this fragrance encapsulates the unrefined beauty
          of expansive landscapes. For the man who yearns for liberation beyond
          the mundane, Dior Sauvage becomes your hallmark—elegant, audacious,
          and unmistakably yours. Unveil the essence of the untamed spirit.
          Indulge in the experience of Dior Sauvage today.
        </p>
      </BailetsBlock>

      <BailetsBlock>
        <h2 className="markTitle">Case Study: Dior Sauvage – Capturing the Untamed Elegance</h2>
        <p style={{ width: '100%' }}>
          <b>Brand:</b> Christian Dior
          <br />
          <br />
          <b>Product:</b> Dior Sauvage Perfume | Pour Homme
          <br />
          <br />
          <b>Objective:</b> To develop persuasive and engaging copywriting for
          Dior Sauvage that captures its essence, differentiates it from
          competitors, and appeals to a diverse target audience.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Challenge</h2>
        <p style={{ width: '100%' }}>
          Dior Sauvage is a distinguished men's fragrance that embodies the
          essence of nature and sophistication. Launched by the iconic fashion
          house Christian Dior, Sauvage aims to appeal to modern men who seek a
          blend of luxury and raw masculinity. Despite its high-quality
          composition and strong brand reputation, Dior faced the challenge of
          creating compelling copywriting that would effectively communicate the
          unique allure of Sauvage and resonate with its target audience.
          <br />
          <br />
          Key Issues:
          <br />
          <b>1- Translating the Scent into Words:</b> The fragrance profile of
          Dior Sauvage is complex, combining fresh bergamot, spicy Sichuan
          pepper, and woody ambroxan. Communicating this intricate blend in a
          way that evokes the same sensory experience is challenging.
          <br />
          <br />
          <b>2- Aligning with Brand Identity:</b> The copy must reflect Dior's
          legacy of luxury and sophistication while also embracing the
          adventurous spirit that Sauvage represents.
          <br />
          <br />
          <b>3- Standing Out in a Crowded Market:</b> With many competitors
          offering similar fresh and woody fragrances, it was crucial to
          highlight what sets Sauvage apart.
          <br />
          <br />
          <b>4- Appealing to a Broad Demographic:</b> The target audience ranges
          from young professionals to mature, affluent men, requiring a message
          that resonates broadly yet feels personal.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Solution</h2>
        <p style={{ width: '100%' }}>
          To address these challenges, Dior implemented a multi-faceted approach
          to their copywriting strategy:
          <br />
          <br />
          <b>1- Sensory and Descriptive Language:</b>
          <br />
          The copy was crafted to vividly describe the fragrance's scent
          profile, using sensory and emotive language. Phrases like "the vibrant
          zest of Calabria bergamot" and "the smoky allure of ambroxan" were
          employed to paint an olfactory picture, making the scent almost
          tangible to the reader.
          <br />
          <br />
          <b>2- Embodying the Brand's Heritage and Spirit:</b>
          <br />
          By emphasizing Dior's craftsmanship and the inspiration behind
          Sauvage, the copy aligned with the brand's luxurious and sophisticated
          image. Statements such as "Crafted with the precision and artistry of
          Dior" highlighted the brand's heritage, while "an ode to the raw
          beauty of nature" captured the fragrance's adventurous spirit.
          <br />
          <br />
          <b>3- Highlighting Unique Elements:</b>
          <br />
          The copy focused on what makes Sauvage unique, including its
          high-quality ingredients and the inspiration from nature. This
          differentiation was key in setting it apart from competitors. The
          narrative element, such as "Inspired by wide-open spaces and the
          rugged elegance of the wild," added depth to the product story.
          <br />
          <br />
          <b>4- Connecting with the Target Audience:</b>
          <br />
          Understanding the target demographic's values and aspirations, the
          copy was designed to speak directly to them. By framing Sauvage as a
          symbol of freedom and sophistication, it appealed to both younger and
          older men. Phrases like "For the man who seeks freedom beyond the
          ordinary" and "your signature—sophisticated, daring, and undeniably
          you" resonated with their desire for a distinctive personal brand.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Results</h2>
        <p style={{ width: '100%' }}>
          The revamped copywriting for Dior Sauvage achieved several notable
          outcomes:
          <br />
          <br />
          <b>1- Increased Engagement:</b> The evocative and descriptive language
          captured the audience's attention, resulting in higher engagement
          rates on digital platforms and in print media.
          <br />
          <br />
          <b>2- Enhanced Brand Perception:</b> Aligning the copy with Dior's
          legacy and the adventurous spirit of Sauvage strengthened the brand's
          image as both luxurious and dynamic.
          <br />
          <br />
          <b>3- Competitive Differentiation:</b> Highlighting the unique aspects
          of Sauvage helped differentiate it from similar fragrances, leading to
          an increase in market share.
          <br />
          <br />
          <b>4- Broader Appeal:</b> The targeted messaging resonated with a wide
          demographic, from young professionals to mature men, boosting overall
          sales and customer loyalty.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Conclusion</h2>
        <p style={{ width: '100%' }}>
          The successful copywriting strategy for Dior Sauvage demonstrates the
          importance of capturing the essence of a product through sensory
          language, aligning with brand identity, differentiating from
          competitors, and connecting with the target audience. By addressing
          these challenges with a thoughtful and strategic approach, Dior
          Sauvage solidified its position as a leading men's fragrance,
          embodying the untamed elegance that it set out to represent.
        </p>
      </BailetsBlock>
    </>
  );
};
